/**
 * @voltxampere/fx — FxFrame computation (Sprint 29-2)
 *
 * Glue between the wire current solver (29-2) and the FxFrame contract
 * the renderer consumes. Sprint 29-4 plugs in the log-scale glow mapping
 * via `computeWireGlow`; until then the field stays 0 (renderer falls
 * back to the static base opacity).
 *
 * Copyright © 2026 360 Studio
 * Licensed under MIT (see LICENSE file in repo root)
 */
import { ledBrightnessFromCurrent, ledColorHex, } from "./components/led-brightness.js";
import { solveWireCurrents, } from "./solvers/wire-current.js";
import { computeWireGlow } from "./wire/glow.js";
/** Below this magnitude, current is treated as zero (no perceptible flow). */
const I_NOISE_FLOOR = 1e-12;
/**
 * Build the per-frame fx state from the engine's output.
 *
 * Returns an `FxFrame` with one `WireFxState` per circuit wire. The
 * `unresolved` set from the wire solver is currently swallowed (those
 * wires get `current = 0, direction = 0, glow = 0`); a future Sprint may
 * surface it via `FxFrame.diagnostics` for the inspector to flag
 * dangling wires.
 */
export function computeFxFrame(input) {
    const result = solveWireCurrents(input.circuit, input.measurements);
    return {
        t: input.t,
        wires: buildWireFxStates(input.circuit, result),
        components: buildComponentFxStates(input.circuit, input.measurements),
    };
}
/**
 * Build per-component fx state from the engine's measurements + circuit
 * topology. Sprint 31-2 fills LED entries (brightness + color);
 * Sprint 31-4 + 31-5 add speaker + buzzer audio hints.
 */
function buildComponentFxStates(circuit, measurements) {
    const measMap = new Map(measurements.map((m) => [m.id, m.current]));
    const components = new Map();
    for (const component of circuit.components) {
        const state = buildComponentFxState(component, measMap);
        if (state) {
            components.set(component.id, state);
        }
    }
    return components;
}
function buildComponentFxState(component, measMap) {
    if (component.type === "led") {
        // LEDs only emit light under forward bias. Reverse current → 0
        // brightness (drop sign before passing to the brightness helper).
        const I = measMap.get(component.id) ?? 0;
        const I_forward = Math.max(0, I);
        const colorParam = component.params.color;
        const color = typeof colorParam === "string" && isLedColor(colorParam) ? colorParam : undefined;
        return {
            componentId: component.id,
            type: "led",
            brightness: ledBrightnessFromCurrent(I_forward),
            color: ledColorHex(color),
        };
    }
    // Speaker + Buzzer entries land in Sprint 31-4 / 31-5.
    return null;
}
function isLedColor(value) {
    return (value === "red" ||
        value === "green" ||
        value === "blue" ||
        value === "yellow" ||
        value === "white");
}
function buildWireFxStates(circuit, result) {
    const wires = new Map();
    for (const wire of circuit.wires) {
        const I = result.currents.get(wire.id) ?? 0;
        const direction = directionFromCurrent(I);
        wires.set(wire.id, {
            current: Math.abs(I),
            direction,
            glow: computeWireGlow(I),
        });
    }
    return wires;
}
function directionFromCurrent(I) {
    if (I > I_NOISE_FLOOR)
        return 1;
    if (I < -I_NOISE_FLOOR)
        return -1;
    return 0;
}
//# sourceMappingURL=compute.js.map